import axios from "axios";
import { API_URL } from "src/scenes/App";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { Toast } from "src/components";
import { API_SUFFIX, FILTER_BY } from "./constants";

/**
 * Fetch users for a given type ('employees' or 'clients')
 * Handles formatting like phone numbers and default visibility flags
 */
export const fetchUsers = async (filterByOption, token) => {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    const suffix = API_SUFFIX[filterByOption];

    try {
        const res = await axios.get(`${API_URL}/chats/leadership/${suffix}`, headers);
        const fetchedUsers =
      filterByOption === FILTER_BY.CLIENT ? res.data.clients : res.data.employees || [];

        const formattedUsers = fetchedUsers.map(u => ({
            ...u,
            type: filterByOption === FILTER_BY.CLIENT ? FILTER_BY.CLIENT : FILTER_BY.EMPLOYEE,
            phoneNumber: u.phoneNumber ? formatPhoneNumberIntl(u.phoneNumber) : "",
            visibleToEmployees: false,
            visibleToClients: false,
        }));

        return formattedUsers;
    } catch (error) {
        Toast.error(error.getApiErrorDetails().message);
        return [];
    }
};

/**
 * Submit a new leadership role and assign selected users
 */
export const submitCreateRole = async ({
    roleName,
    selectedUsers,
    employeeUsers,
    clientUsers,
    createLeadershipRole,
    saveAssignedLeadershipRoles,
    refetchRoles,
    token,
    handleClose
}) => {
    const headers = { headers: { Authorization: `Bearer ${token}` } };

    if (!roleName.trim()) {
        Toast.error("Leadership role title is required");
        return;
    }
    if (selectedUsers.length === 0) {
        Toast.error("Please assign at least one user to this leadership role.");
        return;
    }

    const getUserById = (userId) =>
        employeeUsers.find(u => u.uuid === userId) ||
    clientUsers.find(u => u.uuid === userId);

    try {
        const newRole = await createLeadershipRole({
            name: roleName,
            isVisibleToClients: false,
            isVisibleToEmployees: false,
        });

        const allSavePromises = selectedUsers.map(userId => {
            const user = getUserById(userId);
            if (!user) return null;

            const existingRoles = (user.leadershipRoles || []).map(role => ({
                leadershipRoleUuid: role.uuid,
                userUuid: user.uuid,
                isVisibleToEmployees: role.isVisibleToEmployees,
                isVisibleToClients: role.isVisibleToClients,
            }));

            const updatedRoles = [
                ...existingRoles,
                {
                    leadershipRoleUuid: newRole.uuid,
                    userUuid: user.uuid,
                    isVisibleToEmployees: user.isVisibleToEmployees,
                    isVisibleToClients: user.isVisibleToClients,
                },
            ];

            return saveAssignedLeadershipRoles(user.uuid, updatedRoles, headers);
        }).filter(Boolean);

        await Promise.all(allSavePromises);

        await refetchRoles();
        Toast.success("Leadership role created and users assigned successfully.");
        handleClose();
    } catch (error) {
        console.error(error);
    }
};

/**
 * Submit an update to an existing leadership role
 */
export const submitUpdateRole = async ({
    roleName,
    selectedUsers,
    users,
    leadershipRoleToEdit,
    updateLeadershipRole,
    saveAssignedLeadershipRoles,
    refetchRoles,
    token,
    handleClose
}) => {
    const headers = { headers: { Authorization: `Bearer ${token}` } };

    if (!roleName.trim()) {
        Toast.error("Leadership role title is required.");
        return;
    }
    if (selectedUsers.length === 0) {
        Toast.error("Please assign at least one user to this leadership role.");
        return;
    }

    const roleUuid = leadershipRoleToEdit.uuid;

    try {
        await updateLeadershipRole({ ...leadershipRoleToEdit, name: roleName.trim() });

        const allRelevantUsers = users.filter(user => {
            const hadRole = (user.leadershipRoles || []).some(role => role.uuid === roleUuid);
            const isSelected = selectedUsers.includes(user.uuid);
            return hadRole || isSelected;
        });

        const allSavePromises = allRelevantUsers.map(user => {
            let updatedRoles = (user.leadershipRoles || [])
                .filter(role => role.uuid !== roleUuid)
                .map(role => ({
                    leadershipRoleUuid: role.uuid,
                    userUuid: user.uuid,
                    isVisibleToEmployees: role.isVisibleToEmployees,
                    isVisibleToClients: role.isVisibleToClients,
                }));

            if (selectedUsers.includes(user.uuid)) {
                updatedRoles.push({
                    leadershipRoleUuid: roleUuid,
                    userUuid: user.uuid,
                    isVisibleToEmployees: user.isVisibleToEmployees,
                    isVisibleToClients: user.isVisibleToClients,
                });
            }

            return saveAssignedLeadershipRoles(user.uuid, updatedRoles, headers);
        });

        await Promise.all(allSavePromises);

        await refetchRoles();
        Toast.success("Leadership role updated successfully.");
        handleClose();
    } catch (error) {
        console.error(error);
    }
};

/**
 * Delete a leadership role
 */
export const handleDeleteRole = async ({
    leadershipRoleToEdit,
    deleteLeadershipRole
}) => {
    if (window.confirm("Are you sure you want to remove this leadership role?")) {
        deleteLeadershipRole(leadershipRoleToEdit);
    }
};
